import React, { Dispatch, useCallback, useEffect, useState } from "react";
import { IControl } from "sonobello.utilities.react";

import CalendarEventHub from "../types/CalendarEventHub";
import { EventSource, IEvent } from "../types/EventSource";
import { ICalendarEvent } from "../types/ICalendarEvent";
import { ICalendarEventCenter } from "../types/ICalendarEventCenter";
import { ISignalRListenerProps } from "./SignalRConnection";

/** A {@link EventSource} for signalR topics that issue calendar updates.*/
class CalendarTopicSource extends EventSource<ICalendarEventCenter> {
  constructor(centerId: string, custom: ICalendarEventCenter) {
    super(`update_${centerId}`, custom);
  }
}

export interface ICalendarManagerProps extends IControl<IEvent<ICalendarEventCenter, ICalendarEvent>[]> {
  /** Callback to subscribe to the calendar topics for the specified center ids. */
  subscribe: Dispatch<ICalendarEventCenter[]>;
  /** Callback to unsubscribe to the calendar topic for the specified center id. */
  unsubscribe: Dispatch<string>;
}

export type ICalendarSignalRListenerProps = ISignalRListenerProps<ICalendarEventCenter, ICalendarEvent>;

export interface ICalendarSignalRListenerConfig {
  /** The component which is controlled by the SignalR calendar updates. */
  CalendarManager: React.FC<ICalendarManagerProps>;
}

/** Manages SignalR subscriptions and message payloads for Calendar topics.
 * @remarks Swallows message updates that have timestamps older than the current value.
 */
const CalendarSignalRListener: React.FC<ICalendarSignalRListenerProps & ICalendarSignalRListenerConfig> = ({
  value,
  subscribe,
  unsubscribe,
  CalendarManager
}) => {
  const [topicHub, setTopicHub] = useState(new CalendarEventHub(value));

  useEffect(() => setTopicHub(hub => hub.update(value)), [value]);

  const handleSubscribe = useCallback(
    (topicSourcesCustom: ICalendarEventCenter[]) => {
      subscribe(topicSourcesCustom.map(c => new CalendarTopicSource(c.id, c)));
    },
    [subscribe]
  );

  return <CalendarManager unsubscribe={unsubscribe} subscribe={handleSubscribe} value={topicHub.events} />;
};

export default CalendarSignalRListener;
